import React from 'react';
import PropTypes from 'prop-types';
import { ButtonColorProps } from '../../prop-types';
import { sendEvent } from '../../utils/analytics';
import { DefaultButton, IconHolder, TextElement } from './styles';
import iconSrc from '../../utils/iconSrc';

/**
 * Standard button
 */
const Button = React.forwardRef(
  (
    {
      bg,
      icon,
      iconPlacement = 'center',
      fullWidth,
      type = 'button',
      children,
      className,
      disabled,
      onClick,
      as,
      href,
      size = 'regular',
      data,
      ...rest
    },
    ref
  ) => {
    const iconRight = iconPlacement === 'right';

    const ariaAttributes = {};
    if (rest) {
      Object.keys(rest).forEach((key) => {
        if (key.startsWith('aria-')) {
          ariaAttributes[key] = rest[key];
        }
      });
    }

    const dataAttributes = {};
    if (data) {
      Object.keys(data).forEach((key) => {
        dataAttributes[`data-${key}`] = data[key];
      });
    }

    return (
      <DefaultButton
        className={className}
        type={type}
        disabled={disabled}
        {...dataAttributes}
        onClick={(e) => {
          sendEvent({
            event: 'buttonClick',
          });
          if (onClick) onClick(e);
        }}
        as={as}
        href={href}
        $bg={bg}
        $size={size}
        $iconPlacement={iconPlacement}
        $fullWidth={fullWidth}
        ref={ref}
        {...ariaAttributes}
        {...rest}
      >
        {icon && !iconRight && (
          <IconHolder
            src={iconSrc(icon)}
            $size={size}
            cacheRequests
            aria-hidden="true"
          />
        )}

        {children ? <TextElement $size={size}>{children}</TextElement> : null}

        {icon && iconRight && (
          <IconHolder
            src={iconSrc(icon)}
            $size={size}
            cacheRequests
            aria-hidden="true"
          />
        )}
      </DefaultButton>
    );
  }
);

Button.displayName = 'Button';

Button.propTypes = {
  /**
   * One of the standard theme colors
   */
  bg: ButtonColorProps,
  icon: PropTypes.string,
  iconPlacement: PropTypes.oneOf(['left', 'center', 'right']),
  type: PropTypes.string,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  children: PropTypes.node,
  className: PropTypes.string,
  onClick: PropTypes.func,
  as: PropTypes.string,
  href: PropTypes.string,
  size: PropTypes.oneOf(['regular', 'small', 'xl', 'xxl']),
  data: PropTypes.object,
};

export default Button;
